import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import client from '../../API';
import './TwoFactorModal.css';
import {
  setDeviceToken,
  mapEmailToIdentifier,
} from '../../Utils/deviceTokenStorage';

const TwoFactorModal = ({
  isOpen,
  onClose,
  email,
  userId,
  creatorUsername,
  clientId,
  twofaToken,
  phoneAvailable,
  emailAvailable,
  preferredMethod,
  onSuccess
}) => {
  const [method, setMethod] = useState(preferredMethod || 'email');
  const [otp, setOtp] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [retryCooldown, setRetryCooldown] = useState(0);
  const [hasSwitchedToFallback, setHasSwitchedToFallback] = useState(false);
  const [codeSentForMethod, setCodeSentForMethod] = useState(null);  // 'sms', 'email', or null
  const [methodsWithSentCodes, setMethodsWithSentCodes] = useState({ sms: false, email: false });  // Track which methods have sent codes
  const [switchCooldownWarning, setSwitchCooldownWarning] = useState(null);  // Non-blocking cooldown warning message
  const [pendingFallbackToEmail, setPendingFallbackToEmail] = useState(false);  // Flag for automatic fallback
  const [isManualSwitch, setIsManualSwitch] = useState(false);  // Flag for manual method switching
  const [isSendingInBackground, setIsSendingInBackground] = useState(false);  // Flag for background sending

  const getTwofaAuthConfig = () => {
    if (!twofaToken) return undefined;
    return {
      ignoreAuthError: true,
      headers: {
        Authorization: `Bearer ${twofaToken}`,
      },
    };
  };

  useEffect(() => {
    if (isOpen && !otpSent) {
      sendOTP();
    }
  }, [isOpen]);  // Only trigger on modal open, not method change

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (retryCooldown > 0) {
      const timer = setTimeout(() => {
        setRetryCooldown(retryCooldown - 1);

        // Update cooldown warning message if it's showing
        if (switchCooldownWarning) {
          if (retryCooldown - 1 > 0) {
            setSwitchCooldownWarning(`You can enter your code, but please wait ${retryCooldown - 1} seconds before requesting a new one.`);
          } else {
            setSwitchCooldownWarning(null);  // Clear when cooldown ends
          }
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [retryCooldown, switchCooldownWarning]);

  // Handle automatic fallback to email after method state has updated
  useEffect(() => {
    if (pendingFallbackToEmail && method === 'email') {
      setPendingFallbackToEmail(false);
      // Now guaranteed that method === 'email' in sendOTP()
      sendOTP();
    }
  }, [method, pendingFallbackToEmail]);

  // Validate identifiers are present when modal opens
  useEffect(() => {
    const hasIdentifier = userId || creatorUsername || clientId;

    if (!hasIdentifier && isOpen) {
      console.error('[2FA] CRITICAL: No user identifiers provided!', {
        userId, creatorUsername, clientId
      });
      console.error('[2FA] Device token will NOT be saved if user completes 2FA');
    } else if (isOpen) {
      console.log('[2FA] User identifiers present:', {
        user_id: !!userId,
        creator_username: !!creatorUsername,
        client_id: !!clientId
      });
    }
  }, [userId, creatorUsername, clientId, isOpen]);

  const sendOTP = async () => {
    // Check cooldown before allowing retry - don't show blocking error
    if (retryCooldown > 0) {
      console.log(`[2FA] Cooldown active: ${retryCooldown}s remaining`);
      // Still prevent API call, but don't block UI with error message
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build payload
      const payload = {};
      if (userId) payload.user_id = userId;
      else if (creatorUsername) payload.creator_username = creatorUsername;
      else if (clientId) payload.client_id = clientId;

      // Use centralized API client - this will use the correct base URL
      const response = method === 'sms'
        ? await client.twofa.sendSMS(payload, getTwofaAuthConfig() || {})
        : twofaToken
          ? await client.twofa.sendEmail({}, getTwofaAuthConfig())
          : await client.twofa.sendEmail(payload);

      if (response.success) {
        setOtpSent(true);
        setCountdown(method === 'sms' ? 300 : 600); // 5 or 10 minutes
        // Set retry cooldown: 30 seconds for SMS, 60 for email
        setRetryCooldown(method === 'sms' ? 30 : 60);
        setCodeSentForMethod(method);  // Track which method sent the code
        // Track that this method has successfully sent a code
        setMethodsWithSentCodes(prev => ({
          ...prev,
          [method]: true
        }));
      }
    } catch (err) {
      if (err.response?.data?.fallback === 'email' && !hasSwitchedToFallback) {
        // Phone not available, attempt to switch to email
        // Check if email is actually available before attempting fallback
        if (emailAvailable) {
          setHasSwitchedToFallback(true);
          setMethod('email');
          setError('No phone number on file. Switching to email verification.');
          // Don't call sendOTP() directly - let useEffect handle it after state updates
          setPendingFallbackToEmail(true);
        } else {
          // Email not available - show error and stop
          setHasSwitchedToFallback(true);
          setError('No phone number or email on file. Please contact support.');
        }
      } else if (err.response?.data?.fallback === 'email' && hasSwitchedToFallback) {
        // Already switched, don't loop
        setError('No phone number on file. Please contact support.');
      } else {
        // Check if rate limit error
        const errorMsg = err.response?.data?.error || 'Failed to send OTP';

        // Provide specific error messages
        if (errorMsg.includes('Too many requests')) {
          setError('Rate limit exceeded. Please wait 5 minutes before trying again.');
          setRetryCooldown(300); // 5 minute cooldown on rate limit
        } else if (errorMsg.includes('Invalid phone')) {
          setError('Invalid phone number. Please contact support.');
        } else if (errorMsg.includes('unreachable')) {
          setError('Phone number unreachable. Please try email verification.');
        } else {
          setError(errorMsg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const sendOTPInBackground = async (targetMethod) => {
    if (retryCooldown > 0) {
      console.log(`[2FA] Cooldown active: ${retryCooldown}s remaining`);
      return;
    }

    setIsSendingInBackground(true);
    setError('');

    try {
      const payload = {};
      if (userId) payload.user_id = userId;
      else if (creatorUsername) payload.creator_username = creatorUsername;
      else if (clientId) payload.client_id = clientId;

      // Use targetMethod parameter instead of state variable
      const response = targetMethod === 'sms'
        ? await client.twofa.sendSMS(payload, getTwofaAuthConfig() || {})
        : twofaToken
          ? await client.twofa.sendEmail({}, getTwofaAuthConfig())
          : await client.twofa.sendEmail(payload);

      if (response.success) {
        setCountdown(targetMethod === 'sms' ? 300 : 600);
        setRetryCooldown(targetMethod === 'sms' ? 30 : 60);
        setCodeSentForMethod(targetMethod);
        setMethodsWithSentCodes(prev => ({
          ...prev,
          [targetMethod]: true
        }));
        setError('');  // Clear any previous errors
      }
    } catch (err) {
      // Handle errors inline without blocking UI
      const errorMsg = err.response?.data?.error || 'Failed to send OTP';
      setError(`Error: ${errorMsg}`);
    } finally {
      setIsSendingInBackground(false);
      setIsManualSwitch(false);
    }
  };

  const verifyOTP = async () => {
    console.log('[2FA Debug] verifyOTP called');
    console.log('[2FA Debug] otp value:', otp);
    console.log('[2FA Debug] otp length:', otp.length);
    console.log('[2FA Debug] userId:', userId);
    console.log('[2FA Debug] creatorUsername:', creatorUsername);
    console.log('[2FA Debug] clientId:', clientId);
    console.log('[2FA Debug] method:', method);
    console.log('[2FA Debug] rememberDevice:', rememberDevice);

    if (otp.length !== 6) {
      console.log('[2FA Debug] OTP length check FAILED - returning early');
      setError('Please enter a 6-digit code');
      return;
    }

    console.log('[2FA Debug] OTP length check passed - proceeding to API call');
    setLoading(true);
    setError('');

    try {
      // Build payload
      const payload = {
        otp: otp,
        remember_device: rememberDevice,
        platform: Capacitor.getPlatform()
      };

      if (userId) payload.user_id = userId;
      else if (creatorUsername) payload.creator_username = creatorUsername;
      else if (clientId) payload.client_id = clientId;

      console.log('[2FA Debug] Payload:', payload);
      console.log('[2FA Debug] About to call API...');

      // Use centralized API client
      const response = method === 'sms'
        ? await client.twofa.verifySMS(payload, getTwofaAuthConfig() || {})
        : twofaToken
          ? await client.twofa.verifyEmail(payload, getTwofaAuthConfig())
          : await client.twofa.verifyEmail(payload);

      console.log('[2FA Debug] API response:', response);

      if (response.success) {
        console.log('[2FA Debug] Verification successful, calling onSuccess');
        // Only store device token if user explicitly checked "remember device"
        if (response.device_token) {
          if (rememberDevice) {
            const identifiers = {
              user_id: userId,
              creator_username: creatorUsername,
              client_id: clientId
            };

            // Validate at least one identifier exists
            const hasIdentifier = userId || creatorUsername || clientId;

            if (!hasIdentifier) {
              console.error('[2FA] Cannot store device token - no identifiers!', identifiers);
              setError('Warning: Device will not be remembered. Please contact support.');
            } else {
              // Store in new multi-token storage
              setDeviceToken(identifiers, response.device_token);

              // Create email → identifier mapping for future logins
              if (email) {
                mapEmailToIdentifier(email, identifiers);
              }

              console.log('[2FA Debug] Device token stored (rememberDevice=true):', {
                ...identifiers,
                has_user_id: !!userId,
                has_creator_username: !!creatorUsername,
                has_client_id: !!clientId
              });
            }
          } else {
            console.log('[2FA Debug] Device token NOT stored (rememberDevice=false)');
          }
        }

        // Pass method and user identifiers to onSuccess for confirmation
        onSuccess({
          ...response,
          method,
          user_id: userId,
          creator_username: creatorUsername,
          client_id: clientId
        });
        handleClose();
      }
    } catch (err) {
      console.error('[2FA Debug] Error caught:', err);
      const errorMsg = err.response?.data?.error || 'Invalid or expired code';

      // If SMS verification unavailable, suggest email
      if (errorMsg.includes('temporarily unavailable') && method === 'sms') {
        setError('SMS verification unavailable. Please try Email verification.');
        // Auto-switch to email method after 2 seconds
        setTimeout(() => setMethod('email'), 2000);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = () => {
    const newMethod = method === 'sms' ? 'email' : 'sms';

    // Check if code already sent to new method FIRST
    const hasCodeForNewMethod = methodsWithSentCodes[newMethod];

    // Clear input and errors
    setOtp('');
    setError('');

    // Update method
    setMethod(newMethod);

    if (hasCodeForNewMethod) {
      // Code already sent - show form immediately
      setOtpSent(true);
      setCodeSentForMethod(newMethod);

      if (retryCooldown > 0) {
        setSwitchCooldownWarning(
          `You can enter your code, but please wait ${retryCooldown} seconds before requesting a new one.`
        );
      }
    } else {
      // First time switching to this method - send in background
      setIsManualSwitch(true);
      // CRITICAL: Keep otpSent=true so form stays visible
      setOtpSent(true);
      // Send code in background without blocking UI
      sendOTPInBackground(newMethod);
    }
  };

  const handleClose = () => {
    setHasSwitchedToFallback(false);
    setPendingFallbackToEmail(false);
    setIsManualSwitch(false);
    setIsSendingInBackground(false);
    setCodeSentForMethod(null);
    setMethodsWithSentCodes({ sms: false, email: false });  // Reset tracking
    setSwitchCooldownWarning(null);  // Clear cooldown warning
    onClose();
  };

  const handleResend = () => {
    if (retryCooldown > 0) {
      // Don't show error - button should be disabled anyway
      console.log(`[2FA] Cannot resend: ${retryCooldown}s cooldown remaining`);
      return;
    }
    setOtpSent(false);
    setOtp('');
    setError('');
    sendOTP();
  };

  if (!isOpen) return null;

  return (
    <div className="twofa-modal-overlay" onClick={handleClose}>
      <div className="twofa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="twofa-modal-header">
          <h2>Verify Your Identity</h2>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>

        <div className="twofa-modal-body">
          {/* Only show loading screen on INITIAL send, not during manual switches */}
          {(!otpSent && !isManualSwitch) ? (
            <div className="sending-otp">
              {!error ? (
                <>
                  <div className="spinner"></div>
                  <p>Sending verification code...</p>
                </>
              ) : (
                <div className="error-message" style={{ color: '#d32f2f', padding: '20px', textAlign: 'center' }}>
                  <p style={{ marginBottom: '15px' }}>{error}</p>
                  <button
                    onClick={() => { setError(''); sendOTP(); }}
                    disabled={retryCooldown > 0}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: retryCooldown > 0 ? '#ccc' : '#7b2a2a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: retryCooldown > 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {retryCooldown > 0 ? `Retry (${retryCooldown}s)` : 'Retry'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Add inline loading indicator for background sends */}
              {isSendingInBackground && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  marginBottom: '10px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#1976d2'
                }}>
                  Sending code...
                </div>
              )}

              <p className="instruction">
                {method === 'sms'
                  ? 'Enter the 6-digit code sent to your phone'
                  : 'Enter the 6-digit code sent to your email'}
              </p>

              <div className="otp-input-container">
                <input
                  type="text"
                  className="otp-input"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    if (switchCooldownWarning) setSwitchCooldownWarning(null);  // Clear warning when user starts typing
                  }}
                  maxLength={6}
                  disabled={loading}
                  autoFocus
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              {/* Non-blocking cooldown warning for method switching */}
              {switchCooldownWarning && !error && (
                <div className="cooldown-warning" style={{
                  marginTop: '10px',
                  padding: '10px 15px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  color: '#856404',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  ⏱️ {switchCooldownWarning}
                </div>
              )}

              {/* Non-blocking cooldown info banner */}
              {!error && !switchCooldownWarning && retryCooldown > 0 && (
                <div className="cooldown-info" style={{
                  marginTop: '10px',
                  padding: '10px 15px',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '4px',
                  color: '#1565c0',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  ℹ️ You can enter your code now. New code requests available in {retryCooldown}s.
                </div>
              )}

              <div className="countdown">
                {countdown > 0
                  ? `Code expires in ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`
                  : 'Code expired'}
              </div>

              <div className="remember-device">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                  <span>Remember this device for 30 days</span>
                </label>
              </div>

              <button
                className="verify-button"
                onClick={verifyOTP}
                disabled={loading || otp.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <div className="action-buttons">
                <button
                  className="text-button"
                  onClick={handleResend}
                  disabled={retryCooldown > 0 || loading}
                >
                  {retryCooldown > 0 ? `Resend Code (${retryCooldown}s)` : 'Resend Code'}
                </button>

                {((method === 'sms' && emailAvailable) || (method === 'email' && phoneAvailable)) && (
                  <button
                    className="text-button"
                    onClick={switchMethod}
                    disabled={loading}
                  >
                    Use {method === 'sms' ? 'Email' : 'SMS'} Instead
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;
